/**
 * react-admin store configuration — single source of truth for the app key
 * and the derived localStorage key names.
 *
 * ra-core's localStorageStore(version, appKey) prefixes every key with
 * `RaStore${appKey}` (see node_modules ra-core/src/store/localStorageStore.ts:
 * `const prefix = \`${RA_STORE}${appKey}\``). With appKey 'vetpo' the real
 * stored key for useStore('glucoseUnit') is therefore `RaStorevetpo.glucoseUnit`
 * — NOT `RaStore.glucoseUnit`.
 *
 * That one-character-class mismatch was the deepest root cause of the repeat
 * "glucose units don't switch" reports (Jun 06/26 issue list, V1): every
 * authProvider seeding write targeted the unprefixed dead key, so login
 * seeding (and the geolocation default) had NEVER taken effect — only the
 * profile dialog's setGlucoseUnit (store API → correct key) ever moved the
 * charts. Both prior fix attempts (748e42f, fb238db) tuned logic that was
 * writing to a key nothing reads.
 *
 * Anything that touches RA store keys outside the store API must import from
 * here.
 */
export const RA_STORE_APP_KEY = 'vetpo'

/** Prefix ra-core puts on every persisted key for this app. */
export const RA_STORE_PREFIX = `RaStore${RA_STORE_APP_KEY}`

/** localStorage key backing useStore('glucoseUnit', ...) */
export const GLUCOSE_UNIT_STORAGE_KEY = `${RA_STORE_PREFIX}.glucoseUnit`

/**
 * Remove the USER-scoped RA store keys from localStorage.
 *
 * Why: the auth token lives in sessionStorage (dies with the tab) but the RA
 * store lives in localStorage (survives forever). On shared staff browsers a
 * stale `selectedPetId`/`selectedDeviceId` from one account is restored into
 * the NEXT account's dashboard, which fires the full pet fan-out
 * (/api/pets/:id, devices, emrs, metrics, activities) before any click —
 * Payload masks the access denial as 404, producing weeks of
 * "[Payload Error] Not Found [collection=pets ... role=vet]" noise in prod
 * (diagnosed 2026-06-06: vet 1167's three weeks of 404s were all
 * cross-account localStorage leakage).
 *
 * Called from BOTH authProvider.login (covers the close-tab-without-logout
 * path) and authProvider.logout. Deliberately NOT store.reset(): device-level
 * preferences (themeName, sidebar.open, locale) are harmless across users
 * and resetting them would visibly churn shared machines on every login.
 *
 * glucoseUnit is ALSO treated as a device-level preference and intentionally
 * NOT cleared here (product decision 2026-06-13): this browser's last chosen
 * unit persists across login/logout and WINS over the account's cloud profile
 * (`settings.glucoseUnit`), which is only used as the fallback seed when this
 * browser has no choice yet (see authProvider.applyAuthResult). Tradeoff: on a
 * shared clinic browser the previous user's unit carries over — accepted as
 * benign (display-only), unlike the pet/device keys below which cause an
 * unauthorized fan-out and so MUST clear.
 *
 * Direct localStorage removal doesn't notify mounted useStore subscribers,
 * which is fine at these call sites: login/logout happen outside the
 * dashboard, and components re-read storage on next mount.
 */
// The unassigned-patient reminder's 7-day snooze (VP-TEAM-R23). Lives here so
// the clear-on-account-switch sweep and the banner share one definition: the
// snooze is browser-local by design, but master Vet A snoozing on a shared
// machine must not silence master Vet B's reminder after a login switch.
export const ASSIGN_REMINDER_SNOOZE_KEY = 'vetpo.assignReminderSnoozedUntil'

// CompleteClinicProfilePrompt's "Not now" dismissal (sessionStorage, tab-local
// by design). Left uncleared, it survived a logout -> login of a DIFFERENT
// vet in the same tab, so a master vet who genuinely needs the prompt (e.g.
// vet B, still `unverified`) never saw it because vet A had dismissed it
// first. Constant lives here (not in the component) so this sweep and the
// component share one definition — same pattern as ASSIGN_REMINDER_SNOOZE_KEY.
export const CLINIC_PROFILE_PROMPT_DISMISS_KEY = 'clinic_profile_prompt_dismissed'

export const clearUserScopedStoreKeys = () => {
  try {
    localStorage.removeItem(ASSIGN_REMINDER_SNOOZE_KEY)
  } catch {
    // storage unavailable — best-effort, same as the banner's own reads.
  }
  try {
    sessionStorage.removeItem(CLINIC_PROFILE_PROMPT_DISMISS_KEY)
  } catch {
    // sessionStorage may be unavailable in some privacy modes — best-effort.
  }
  localStorage.removeItem(`${RA_STORE_PREFIX}.selectedPetId`)
  // selectedDeviceId is now tab-local (sessionStorage) via perTabKeyStore, so
  // clear it there on a same-tab account switch. The legacy localStorage remove
  // stays as belt-and-braces: it purges any stale value written by an older
  // build before the store moved to sessionStorage.
  localStorage.removeItem(`${RA_STORE_PREFIX}.selectedDeviceId`)
  try {
    sessionStorage.removeItem(`${RA_STORE_PREFIX}.selectedDeviceId`)
  } catch {
    // sessionStorage may be unavailable in some privacy modes — best-effort.
  }
  // Per-pet target ranges (`pet.{id}.targetRangeLow/High`) are patient
  // clinical context — clear the whole family. Also clear the UNprefixed
  // `glucoseRange_<petId>` offline-fallback cache written by
  // AlertRulesSection: it holds a patient's clinical range and is PATCHed
  // back to the server on next mount, so if it survives account switch the
  // next user reads it AND replays it under their own session.
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key && (key.startsWith(`${RA_STORE_PREFIX}.pet.`) || key.startsWith('glucoseRange_'))) {
      localStorage.removeItem(key)
    }
  }
}
